import axios, { AxiosError, AxiosResponse } from 'axios';
import { stringify } from '@utiljs/param';
import { getCookie } from '@utiljs/cookie';
// import UA from '@common/utils/ua';
import utils from './utils';

export interface IServerResponse<Data = any> {
    code: number;
    msg?: string;
    data: Data;
}

export type RequestError<T> = Error | AxiosError<IServerResponse<T>>;

export const { isAxiosError } = axios;

const env: any = process.env.APP_ENV;
function getHost() {
    const urlMap: any = {
        development: 'http://platformproxy.waimai.dev.sankuai.com',
        qa: 'http://e.platform.proxy.b.waimai.test.sankuai.com',
        stage: 'https://proxy.waimai.st.sankuai.com',
        master: 'https://waimaieapp.meituan.com',
    };
    return urlMap[env] ? urlMap[env] : urlMap.master;
}

// 默认设置
axios.defaults.headers.post['Content-Type'] = 'application/x-www-form-urlencoded';
axios.defaults.timeout = 60 * 1000; // 默认超时时间60s

// 通过对 `axios.interceptors.response` 的拦截设置，将「请求本身成功，但业务 `code` 不为0」封装为 AxiosError
// 这样，业务方可以在 catch 函数或语句中处理错误
// 而且可以通过 `error.response?.data` 获取原始数据（需要首先用 `isAxiosError` 判断是否是 AxiosError）
axios.interceptors.response.use(
    (response: AxiosResponse<IServerResponse>) => {
        const { data } = response;
        if (data.code !== 0) {
            // 将 error 封装为 AxiosError，便于调用者统一错误处理
            const error = new Error(data.msg) as AxiosError<IServerResponse>;

            error.response = response;
            error.request = response.request;
            error.config = response.config;
            error.isAxiosError = true;

            // 参照 axios/lib/core/enhanceError.js 原生的 toJSON 函数实现，只保留了 error 的标准属性和 axios 的属性
            error.toJSON = () => ({
                message: error.message,
                name: error.name,
                config: error.config,
                code: error.code,
            });
            return Promise.reject(error);
        }
        return response;
    },
    error => Promise.reject(error),
);

/**
 * @param url
 * @param method get/post
 * @param params 参数
 * @param config 其他配置，如：{timeout: 2000, headers: {'X-Requested-With': 'XMLHttpRequest'}}
 * @param toastOnError 默认为 `true`。此参数为 `true` 时，会在请求错误或业务 `code` 不为0时弹出相应的toast；
 *                     若调用者不需要默认的 toast 行为，根据业务 `code` 自定义 toast 的文案和样式等，可以传入 `false`
 */
function ajax<T>(url: string, method: string, params: any, config: any, toastOnError = true) {
    const options: any = {
        ...config,
        method,
        url,
        // withCredentials: true,
    };

    if (method === 'get') {
        options.params = params;
    } else if (method === 'post') {
        if (config.headers && config.headers['Content-Type'] === 'application/json') {
            options.data = params;
        } else {
            options.data = stringify(params);
        }
    }

    return (
        axios(options)
            .then(response => {
                if (response.status >= 200 && response.status < 300) {
                    return response.data;
                }
                throw new Error(response.statusText);
            })
            // 这里的 `data` 一定是「请求成功」的数据（200 <= status < 300，业务 `code` 也为0）
            .then((data: IServerResponse<T>) => data)

            // 以下是一些默认的错误处理行为
            .catch((error: RequestError<T>) => {
                if (toastOnError && error.message.indexOf('timeout') > -1) {
                    utils.toast('error', '请求超时，请重试~');
                }

                // 如果不是 `AxiosError` 或者 `error.response` 为空，则无需进一步的错误处理
                if (!axios.isAxiosError(error) || !error.response) throw error;

                const {
                    data: { code: businessCode, msg: businessMessage },
                } = error.response;
                switch (businessCode) {
                    case 9001:
                        if (url !== '/api/marginAccount/status') {
                            window.location.href = `${getHost()}/finance/common/pc/accountRecharge/rechargeToInfo?${stringify(
                                {
                                    successCallback: window.location.href,
                                    token: getCookie('b_token'),
                                    acctId: getCookie('a_id'),
                                    serviceProviderID: getCookie('serviceProviderID'),
                                    deviceUuid: getCookie('device_uuid'),
                                    _source: 'providerPC',
                                    acctType: 1038,
                                },
                            )}`;
                        }
                        throw error;
                    case 2001:
                        setTimeout(() => {
                            window.location.href = '/logout/';
                        }, 1500);
                        throw error;
                    default:
                        if (toastOnError) utils.toast('error', businessMessage || '数据请求错误~');
                        throw error;
                }
            })
    );
}

const get = <T = any>(
    url: string,
    params: any = {},
    config: any = {},
    toastOnError = true,
): Promise<IServerResponse<T>> => ajax(url, 'get', params, config, toastOnError);

const post = <T = any>(
    url: string,
    params: any = {},
    config: any = {},
    toastOnError = true,
): Promise<IServerResponse<T>> => ajax(url, 'post', params, config, toastOnError);

export default {
    get,
    post,
};
